import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  AlertTriangle, ArrowDown, ArrowLeftRight, Check, ChevronRight, Code2, Copy, Download, Edit3,
  Loader2, MessageSquare, Pencil, Plus, RefreshCw, Rocket, Search, Send, Settings2,
  Square, Star, Trash2, X, Zap
} from "lucide-react";
import { api, useAuth } from "../lib/api";
import { Badge, Button, Card, CanaryBadge, Label, Modal, Mono, PageHeader, Skeleton } from "../components/ui";
import { KeyForm, UseKey } from "../components/keys/KeyKit";
import { Segmented } from "../components/ui/shared";
import { UC_ICONS } from "./UseCases";
import MarkdownMessage from "../components/MarkdownMessage";

/* ─────── streaming helper: ALWAYS the OpenAI-compatible gateway (POST /api/v1/chat/completions, model = endpoint name) ─────── */
export const GATEWAY_URL = `${process.env.REACT_APP_BACKEND_URL}/api/v1/chat/completions`;
/** runAs: { kind: "me" } → signed-in session identity (server-side, no limits) · { kind: "key", secret } → that key's limits apply. */
export async function streamChat(body, onDelta, signal, runAs = { kind: "me" }) {
  const t0 = performance.now(); let first = null; let n = 0; let text = ""; let usage = null;
  const bearer = runAs.kind === "key" ? runAs.secret : localStorage.getItem("cx_token");
  const headers = { "Content-Type": "application/json", Authorization: `Bearer ${bearer}` };
  const tenant = localStorage.getItem("cx_tenant"); if (tenant && runAs.kind !== "key") headers["X-CoreX-Tenant"] = tenant;
  const payload = { ...body, stream: true };
  const request = { method: "POST", url: GATEWAY_URL, headers: { ...headers, Authorization: runAs.kind === "key" ? `Bearer cx_…${(runAs.secret || "").slice(-4)} [redacted]` : "Bearer [redacted — your signed-in session token]" }, body: payload, run_as: runAs.kind === "key" ? `API key …${(runAs.secret || "").slice(-4)}` : "signed-in user (playground identity, no limits)" };
  const res = await fetch(GATEWAY_URL, { method: "POST", headers, body: JSON.stringify(payload), signal });
  const respHeaders = {}; res.headers.forEach((v, k) => { respHeaders[k] = v; });
  if (!res.ok) {
    const j = await res.json().catch(() => ({}));
    const msg = j.error?.message || j.detail || `HTTP ${res.status}`;
    const err = new Error(msg); err.status = res.status; err.request = request; err.response = { status: res.status, headers: respHeaders, body: j }; throw err;
  }
  const reader = res.body.getReader(); const dec = new TextDecoder(); let buf = "";
  try {
    for (;;) {
      if (signal?.aborted) { reader.cancel(); break; }
      const { value, done } = await reader.read(); if (done) break;
      buf += dec.decode(value, { stream: true }); const lines = buf.split("\n"); buf = lines.pop();
      for (const ln of lines) {
        if (!ln.startsWith("data:")) continue; const p = ln.slice(5).trim(); if (!p || p === "[DONE]") continue;
        try {
          const j = JSON.parse(p); if (j.usage) usage = j.usage;
          if (j.error) { const err = new Error(j.error.message || "upstream error"); err.status = 502; throw err; }
          const c = j.choices?.[0]?.delta?.content;
          if (c) { if (first === null) first = performance.now(); n += 1; text += c; onDelta(text); }
        } catch (e) { if (e.status) throw e; /* partial JSON */ }
      }
    }
  } catch (e) { if (e.name !== "AbortError") throw e; }
  const total = performance.now() - t0;
  return {
    text, usage,
    ttft_ms: first ? Math.round(first - t0) : null,
    total_ms: Math.round(total),
    tps: first && n > 1 ? +(((usage?.completion_tokens || n) - 1) / ((total - (first - t0)) / 1000)).toFixed(1) : null,
    deployment: res.headers.get("X-CoreX-Deployment"),
    request_id: res.headers.get("X-CoreX-Request-Id"),
    request, response: { status: res.status, headers: respHeaders },
    stopped: signal?.aborted,
  };
}

/* ─────── constants ─────── */
const DEFAULTS = { temperature: 0.7, max_tokens: 256, top_p: 1 };
const PRESETS = [
  { label: "Helpful assistant", value: "You are a helpful assistant." },
  { label: "Concise", value: "You are a helpful, concise assistant. Keep responses short and to the point." },
  { label: "Code helper", value: "You are an expert programmer. Write clean, well-documented code. Explain your reasoning." },
  { label: "JSON only", value: "You are a JSON API. Always respond with valid JSON only, no explanations." },
];

/* ─────── session storage (server-side per user; localStorage is imported once, then only used for pins) ─────── */
const SESS_PREFIX = "cx_pg_sessions_";
const PINS_KEY = "cx_pg_pinned";
const getLocalSessions = (uid) => { try { return JSON.parse(localStorage.getItem(SESS_PREFIX + uid) || "[]"); } catch { return []; } };
const getPinned = () => { try { return JSON.parse(localStorage.getItem(PINS_KEY) || "[]"); } catch { return []; } };
const savePinned = (p) => localStorage.setItem(PINS_KEY, JSON.stringify(p));

/* ─────── sub-components ─────── */

function ModelRail({ models, selected, onSelect, multi, search, setSearch, pinned, onPin, canDeploy }) {
  const filtered = models?.filter((m) => !search || `${m.id} ${m.corex?.use_case_name || ""}`.toLowerCase().includes(search.toLowerCase())) || [];
  const isReady = (m) => m.live !== false && m.corex?.readiness?.ready !== false && m.corex?.routable;
  const ready = filtered.filter(isReady);
  const notReady = filtered.filter((m) => !isReady(m));
  // Within each group, pinned first
  const sort = (list) => [...list.filter((m) => pinned.includes(m.id)), ...list.filter((m) => !pinned.includes(m.id))];

  const renderModel = (m, grey) => {
    const rdy = isReady(m);
    const reason = m.corex?.paused ? "paused by its owner" : m.corex?.readiness?.reason;
    const tps = m.corex?.measured_decode_tps;
    const Icon = UC_ICONS[m.corex?.use_case_icon] || MessageSquare;
    return (
      <div key={m.id} className={`relative group/item ${grey ? "opacity-55" : ""}`} data-testid={`model-rail-item-${m.id}`} aria-disabled={!rdy || undefined}>
        <button type="button" onClick={() => onSelect(m.id)}
          className={`w-full text-left px-3 py-2.5 rounded-lg text-sm transition-colors hover:bg-white/5
            ${selected.includes(m.id) ? (rdy ? "bg-cx-emerald/10 border border-cx-emerald/40" : "bg-white/5 border border-cx-border") : "border border-transparent"}`}>
          <div className="flex items-center gap-1.5 flex-wrap">
            <Icon size={12} className={rdy ? "text-cx-emerald shrink-0" : "text-cx-muted shrink-0"} title={m.corex?.use_case_name || "Custom"} data-testid={`model-rail-icon-${m.id}`} />
            <Mono className="text-xs font-medium">{m.id}</Mono>
            <Badge color={rdy ? "green" : m.corex?.paused ? "gray" : "yellow"} className="!text-[8px] !px-1 !py-0" data-testid={`model-rail-state-${m.id}`}>{rdy ? "Live" : m.corex?.paused ? "Paused" : "Not live"}</Badge>
            {m.corex?.target?.simulated && <CanaryBadge compact />}
            {pinned.includes(m.id) && <Star size={9} className="text-cx-amber fill-cx-amber" />}
            {tps != null && !m.corex?.target?.simulated && <Badge color="gray" className="!text-[8px] !px-1 !py-0">{tps} tok/s</Badge>}
          </div>
          <div className="text-[10px] text-cx-muted mt-0.5 flex items-center gap-1.5 flex-wrap">
            {m.corex?.use_case_name && <span className="truncate">{m.corex.use_case_name}</span>}
            {rdy ? (
              <>
                {m.corex?.measured_ttft_ms != null && !m.corex?.target?.simulated && (
                  <span className="inline-flex items-center gap-0.5"><Zap size={8} className="text-cx-amber" /> {m.corex.measured_ttft_ms} ms</span>
                )}
                {!m.corex?.target?.simulated && m.corex?.gpu_class && <Badge color="cyan" className="!text-[8px] !px-1 !py-0">{m.corex.gpu_class}</Badge>}
              </>
            ) : (
              <span className="text-cx-muted truncate" data-testid={`model-rail-reason-${m.id}`} title={reason}>{reason || "not live"}</span>
            )}
          </div>
        </button>
        <button type="button" onClick={(e) => { e.stopPropagation(); onPin(m.id); }}
          className="absolute top-2 right-2 hidden group-hover/item:flex w-5 h-5 rounded items-center justify-center text-cx-muted hover:text-cx-amber transition-colors"
          title={pinned.includes(m.id) ? "Unpin" : "Pin"} data-testid={`pg-pin-${m.id}`}>
          {pinned.includes(m.id) ? <Star size={11} className="fill-cx-amber text-cx-amber" /> : <Star size={11} />}
        </button>
      </div>
    );
  };

  return (
    <div className="flex flex-col h-full" data-testid="model-rail">
      <div className="px-3 pt-3 pb-2">
        <div className="cx-section-label mb-2">Endpoints</div>
        <div className="relative">
          <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-cx-muted" />
          <input className="cx-input !pl-8 !py-1.5 !text-xs" placeholder="Search endpoints…" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="pg-model-search" />
        </div>
      </div>
      <div className="flex-1 overflow-y-auto px-2 pb-2 space-y-0.5">
        {models === null ? <Skeleton className="h-24 mx-1" /> : filtered.length === 0 && !search ? (
          <div className="flex flex-col items-center justify-center text-center px-3 py-8 gap-3" data-testid="pg-no-models">
            <div className="w-10 h-10 rounded-lg bg-cx-muted/10 flex items-center justify-center">
              <AlertTriangle size={20} className="text-cx-muted" />
            </div>
            <div className="text-xs font-medium">No endpoints yet</div>
            <p className="text-[10px] text-cx-muted leading-relaxed max-w-[180px]">
              Deploy a model first — its endpoint appears here automatically.
            </p>
            {canDeploy ? (
              <Link to="/deployments/new" className="cx-btn cx-btn-primary cx-btn-sm" data-testid="pg-deploy-link">
                <Rocket size={12} /> Deploy a model
              </Link>
            ) : (
              <span className="text-[10px] text-cx-muted">Ask your operator to deploy one.</span>
            )}
          </div>
        ) : filtered.length === 0 && search ? (
          <div className="text-xs text-cx-muted p-3">No endpoints matching &ldquo;{search}&rdquo;.</div>
        ) : (<>
          {sort(ready).length > 0 && (<>
            <div className="text-[9px] uppercase tracking-wider text-cx-emerald font-semibold px-3 pt-1 flex items-center gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-cx-emerald" /> Live
            </div>
            {sort(ready).map((m) => renderModel(m, false))}
          </>)}
          {sort(notReady).length > 0 && (<>
            <div className="text-[9px] uppercase tracking-wider text-cx-muted font-semibold px-3 pt-2 mt-1 border-t border-cx-border">Not live</div>
            {sort(notReady).map((m) => renderModel(m, true))}
          </>)}
        </>)}
      </div>
      {multi && <div className="text-[10px] text-cx-muted px-3 pb-2">Pick two endpoints for Compare.</div>}
    </div>
  );
}

function SessionList({ sessions, activeId, onSelect, onNew, onRename, onDelete }) {
  const [editing, setEditing] = useState(null);
  const [name, setName] = useState("");
  return (
    <div className="border-t border-cx-border px-2 pt-2 pb-1" data-testid="pg-sessions">
      <div className="flex items-center justify-between px-1 mb-1">
        <span className="cx-section-label">Sessions</span>
        <button onClick={onNew} className="cx-btn cx-btn-ghost !p-1" title="New chat" data-testid="pg-new-chat"><Plus size={12} /></button>
      </div>
      <div className="max-h-32 overflow-y-auto space-y-0.5">
        {sessions.map((s) => (
          <div key={s.id} className={`group flex items-center gap-1 px-2 py-1 rounded text-xs cursor-pointer transition-colors
            ${s.id === activeId ? "bg-cx-emerald/10 text-cx-emerald" : "text-cx-muted hover:bg-white/[.04] hover:text-cx-text"}`}
            onClick={() => onSelect(s.id)} data-testid={`pg-session-${s.id}`}>
            <MessageSquare size={11} className="shrink-0" />
            {editing === s.id ? (
              <input className="cx-input !py-0 !px-1 !text-xs flex-1" value={name} onChange={(e) => setName(e.target.value)}
                onBlur={() => { onRename(s.id, name); setEditing(null); }}
                onKeyDown={(e) => { if (e.key === "Enter") { onRename(s.id, name); setEditing(null); } }}
                autoFocus data-testid="pg-session-rename-input" />
            ) : <span className="truncate flex-1">{s.name}</span>}
            <div className="hidden group-hover:flex items-center gap-0.5 shrink-0">
              <button onClick={(e) => { e.stopPropagation(); setEditing(s.id); setName(s.name); }} className="hover:text-cx-text" data-testid={`pg-session-edit-${s.id}`}><Pencil size={10} /></button>
              <button onClick={(e) => { e.stopPropagation(); onDelete(s.id); }} className="hover:text-cx-danger" data-testid={`pg-session-delete-${s.id}`}><Trash2 size={10} /></button>
            </div>
          </div>
        ))}
        {sessions.length === 0 && <div className="text-[10px] text-cx-muted px-2 py-1">No saved sessions.</div>}
      </div>
    </div>
  );
}

/** [View request]: the exact HTTP request sent to the gateway (Authorization redacted) plus response headers/latency. */
function ViewRequest({ r }) {
  const [open, setOpen] = useState(false);
  if (!r?.request) return null;
  return (<>
    <button type="button" onClick={() => setOpen(true)} className="underline hover:text-cx-text" data-testid="pg-view-request">View request</button>
    <Modal open={open} onOpenChange={setOpen} title="Request sent to the gateway" desc={`${r.request.method} ${r.request.url} · run as ${r.request.run_as}`} testid="pg-view-request-modal" wide>
      <pre className="cx-mono text-[11px] p-3 rounded-lg bg-black/60 border border-cx-border overflow-auto max-h-[45vh]" data-testid="pg-view-request-body">{`${r.request.method} ${r.request.url}\n${Object.entries(r.request.headers).map(([k, v]) => `${k}: ${v}`).join("\n")}\n\n${JSON.stringify(r.request.body, null, 2)}`}</pre>
      <div className="text-xs mt-3 mb-1 font-semibold">Response · HTTP {r.response?.status}{r.ttft_ms != null ? ` · first token ${r.ttft_ms} ms` : ""}{r.total_ms != null ? ` · total ${r.total_ms} ms` : ""}</div>
      <pre className="cx-mono text-[11px] p-3 rounded-lg bg-black/60 border border-cx-border overflow-auto max-h-[25vh]" data-testid="pg-view-response-headers">{Object.entries(r.response?.headers || {}).map(([k, v]) => `${k}: ${v}`).join("\n")}{r.response?.body ? `\n\n${JSON.stringify(r.response.body, null, 2)}` : ""}</pre>
    </Modal>
  </>);
}

/** "Connected to" line + Run-as picker: the Playground provably talks to the gateway with the chosen identity. */
function ConnectedTo({ models, sel, runAs, setRunAs }) {
  const [paste, setPaste] = useState(false);
  const [sp] = useSearchParams();
  const [fromKey, setFromKey] = useState(null);
  useEffect(() => { const k = sp.get("key"); if (k) api(`/api-keys/${k}`).then((r) => { setFromKey(r); setRunAs({ kind: "key", secret: "" }); setPaste(true); }).catch(() => {}); }, [sp.get("key")]);
  const desc = (id) => { const m = models.find((x) => x.id === id); const t = m?.corex?.target; return `${id} → ${t?.deployment_name || t?.name || t?.id || "?"}${t?.cluster_name ? ` on ${t.cluster_name}` : ""}`; };
  return (<div className="mb-2 px-3 py-1.5 rounded-lg border border-cx-border bg-white/[.02] flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-cx-muted" data-testid="pg-connected-to">
    <span>Connected to <Mono className="text-cx-text">{sel.length ? sel.map(desc).join("  ·  ") : "no endpoint selected — pick one on the left"}</Mono> · via <Mono>POST /api/v1/chat/completions</Mono>{fromKey && <span data-testid="pg-from-key"> · key <b>{fromKey.name}</b> (…{fromKey.key_hint}) — paste its secret to run with its limits</span>}</span>
    <span className="ml-auto inline-flex items-center gap-1">Run as
      <Segmented size="sm" value={runAs.kind} onChange={(v) => { setRunAs(v === "key" ? { kind: "key", secret: runAs.secret || "" } : { kind: "me" }); setPaste(v === "key"); }} testid="pg-run-as" options={[{ value: "me", label: "Me (no limits)" }, { value: "key", label: "API key" }]} />
      {runAs.kind === "key" && (paste || !runAs.secret ? <input className="cx-input !py-0.5 !text-[11px] !w-56" type="password" placeholder="paste cx_… key (its limits apply)" value={runAs.secret || ""} onChange={(e) => setRunAs({ kind: "key", secret: e.target.value.trim() })} onBlur={() => runAs.secret && setPaste(false)} data-testid="pg-run-as-key" /> : <button type="button" className="underline" onClick={() => setPaste(true)} data-testid="pg-run-as-key-edit">key …{runAs.secret.slice(-4)}</button>)}
    </span>
  </div>);
}

function MsgFooter({ r }) {
  if (!r) return null;
  return (
    <div className="flex items-center gap-2 text-[10px] text-cx-muted mt-1 flex-wrap" data-testid="chat-footer">
      {r.ttft_ms != null && <span>TTFT {r.ttft_ms} ms</span>}
      {r.tps != null && <span>{r.tps} tok/s</span>}
      <span>{r.total_ms} ms</span>
      {r.usage && <span>{r.usage.prompt_tokens}+{r.usage.completion_tokens} tok</span>}
      {r.deployment && <span className="cx-mono">{r.deployment}</span>}
      {r.request && <span className="cx-mono">POST /api/v1/chat/completions · model "{r.request.body?.model}"</span>}
      <ViewRequest r={r} />
      {r.stopped && <Badge color="yellow" className="!text-[8px]">stopped</Badge>}
    </div>
  );
}

function SettingsPanel({ sys, setSys, params, setParams, onReset, collapsed, setCollapsed }) {
  return (
    <div className={`transition-all duration-200 shrink-0 ${collapsed ? "w-0 overflow-hidden" : "w-[300px]"}`} data-testid="pg-settings-panel">
      <Card className="h-full flex flex-col gap-3 !p-4">
        <div className="flex items-center justify-between">
          <span className="text-xs font-semibold uppercase tracking-wider text-cx-muted">Settings</span>
          <button onClick={() => setCollapsed(true)} className="cx-btn cx-btn-ghost !p-1" data-testid="pg-settings-close"><X size={14} /></button>
        </div>
        <div>
          <Label className="mb-1">System prompt</Label>
          <div className="flex gap-1 flex-wrap mb-1.5">
            {PRESETS.map((p) => (
              <button key={p.label} onClick={() => setSys(p.value)} data-testid={`pg-preset-${p.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={`text-[10px] px-2 py-0.5 rounded-md transition-colors ${sys === p.value ? "bg-cx-emerald/15 text-cx-emerald" : "bg-white/[.04] text-cx-muted hover:text-cx-text"}`}>
                {p.label}
              </button>
            ))}
          </div>
          <textarea className="cx-input font-mono text-xs min-h-[80px] resize-y" value={sys} onChange={(e) => setSys(e.target.value)} data-testid="pg-system" />
        </div>
        {[
          { key: "temperature", min: 0, max: 2, step: 0.05, label: "Temperature" },
          { key: "max_tokens", min: 1, max: 4096, step: 1, label: "Max tokens" },
          { key: "top_p", min: 0, max: 1, step: 0.05, label: "Top P" },
        ].map(({ key, min, max, step, label }) => (
          <div key={key}>
            <div className="flex items-center justify-between mb-1">
              <Label className="!mb-0">{label}</Label>
              <span className="cx-mono text-xs text-cx-emerald" data-testid={`pg-${key}-value`}>{params[key]}</span>
            </div>
            <input type="range" min={min} max={max} step={step} value={params[key]}
              onChange={(e) => setParams({ ...params, [key]: +e.target.value })}
              className="w-full accent-[var(--color-cx-emerald)] h-1.5 rounded-full appearance-none bg-white/10 cursor-pointer"
              data-testid={`pg-${key}`} aria-label={label} />
            <div className="flex justify-between text-[9px] text-cx-muted cx-mono mt-0.5"><span>{min}</span><span>{max}</span></div>
          </div>
        ))}
        <button onClick={onReset} className="cx-btn cx-btn-ghost cx-btn-sm mt-auto" data-testid="pg-reset-defaults">
          <RefreshCw size={11} /> Reset to defaults
        </button>
      </Card>
    </div>
  );
}

function EmptyState({ model, onPrompt }) {
  const prompts = [
    "Explain how transformer attention works in simple terms",
    "Write a Python function to merge two sorted arrays",
    "Compare REST and gRPC for microservices",
    "Generate a JSON schema for a user profile API",
  ];
  return (
    <div className="flex-1 flex flex-col items-center justify-center p-6 text-center" data-testid="pg-empty-state">
      <div className="w-12 h-12 rounded-xl bg-cx-emerald/10 flex items-center justify-center mb-4">
        <MessageSquare size={24} className="text-cx-emerald" />
      </div>
      <h3 className="text-base font-semibold mb-1">Start a conversation</h3>
      <p className="text-xs text-cx-muted mb-4">
        Model: <Mono className="text-cx-emerald">{model || "none selected"}</Mono> · Streaming is always on
      </p>
      <div className="grid grid-cols-2 gap-2 max-w-lg w-full">
        {prompts.map((p, i) => (
          <button key={i} onClick={() => onPrompt(p)} data-testid={`pg-suggested-${i}`}
            className="text-left p-3 rounded-lg border border-cx-border bg-black/10 hover:border-cx-emerald/40 hover:bg-cx-emerald/5 transition-all text-xs text-cx-secondary leading-relaxed">
            {p}
          </button>
        ))}
      </div>
    </div>
  );
}

function ExportMenu({ messages, model, onClose }) {
  const exportMd = () => {
    const md = messages.map((m) => `**${m.role}**: ${m.content}`).join("\n\n---\n\n");
    const blob = new Blob([`# Playground — ${model}\n\n${md}`], { type: "text/markdown" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = `playground-${model}-${Date.now()}.md`; a.click();
    onClose();
  };
  const exportJson = () => {
    const blob = new Blob([JSON.stringify({ model, messages, exported_at: new Date().toISOString() }, null, 2)], { type: "application/json" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = `playground-${model}-${Date.now()}.json`; a.click();
    onClose();
  };
  return (
    <div className="absolute bottom-full right-0 mb-1 cx-card cx-card-static !p-1 z-20 w-40" data-testid="pg-export-menu">
      <button onClick={exportMd} className="w-full text-left px-3 py-1.5 rounded text-xs hover:bg-white/[.04]" data-testid="pg-export-md">Export Markdown</button>
      <button onClick={exportJson} className="w-full text-left px-3 py-1.5 rounded text-xs hover:bg-white/[.04]" data-testid="pg-export-json">Export JSON</button>
    </div>
  );
}

/* ─────── main page ─────── */
export default function Playground() {
  const { user } = useAuth();
  const canDeploy = user && ["admin", "operator"].includes(user.role);
  const [models, setModels] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [tab, setTab] = useState("chat");
  const [sel, setSel] = useState([]);
  const [sys, setSys] = useState(PRESETS[0].value);
  const [params, setParams] = useState({ ...DEFAULTS });
  const [msgs, setMsgs] = useState([]);
  const [metrics, setMetrics] = useState({});
  const [runAs, setRunAs] = useState({ kind: "me" });
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [cmp, setCmp] = useState([null, null]);
  const [code, setCode] = useState(false);
  const [keyOpen, setKeyOpen] = useState(false);
  const [settingsCollapsed, setSettingsCollapsed] = useState(false);
  const [search, setSearch] = useState("");
  const [showJump, setShowJump] = useState(false);
  const [editIdx, setEditIdx] = useState(null);
  const [exportOpen, setExportOpen] = useState(false);
  const abortRef = useRef(null);
  const chatRef = useRef();
  const inputRef = useRef();
  const [sp] = useSearchParams();

  // sessions — server-side per user (first load imports the browser's legacy localStorage list once)
  const uid = user?.id || user?.email || "default";
  const [sessions, setSessions] = useState([]);
  const [activeSession, setActiveSession] = useState(null);
  const [pinned, setPinned] = useState(() => getPinned());
  const sessionsRef = useRef([]);
  const syncSessions = useCallback((ss, imported) => {
    sessionsRef.current = ss; setSessions([...ss]);
    api("/playground/sessions", { method: "PUT", body: { sessions: ss.map(({ id, name, messages, model, updated }) => ({ id, name, messages, model, updated })), imported: !!imported } }).catch((e) => toast.error(`Could not save session: ${e.message}`));
  }, []);
  useEffect(() => {
    api("/playground/sessions").then((rows) => {
      const ss = (rows || []).map((r) => ({ id: r.id, name: r.name, messages: r.messages || [], model: r.model, updated: r.updated }));
      const legacy = getLocalSessions("default");
      if (ss.length === 0 && legacy.length > 0) { localStorage.removeItem(SESS_PREFIX + "default"); syncSessions(legacy, true); toast.success(`Imported ${legacy.length} saved chat(s) from this browser`); }
      else { sessionsRef.current = ss; setSessions(ss); }
    }).catch((e) => toast.error(e.message));
  }, [uid, syncSessions]);

  const togglePin = (id) => {
    setPinned((p) => {
      const next = p.includes(id) ? p.filter((x) => x !== id) : [...p, id];
      savePinned(next);
      return next;
    });
  };

  // drag-drop text file into input
  const handleDrop = useCallback((e) => {
    e.preventDefault(); e.stopPropagation();
    const file = e.dataTransfer?.files?.[0];
    if (file && file.type.startsWith("text/")) {
      const reader = new FileReader();
      reader.onload = (ev) => setInput((prev) => prev + (prev ? "\n" : "") + ev.target.result);
      reader.readAsText(file);
    }
  }, []);
  const handleDragOver = useCallback((e) => { e.preventDefault(); }, []);

  const saveCurrentSession = useCallback((m) => {
    if (!m.length) return;
    const ss = [...sessionsRef.current];
    const existing = ss.find((s) => s.id === activeSession);
    if (existing) { existing.messages = m; existing.model = sel[0]; existing.updated = Date.now(); }
    else { const id = Date.now().toString(36); ss.unshift({ id, name: (m[0]?.content || `Chat ${ss.length + 1}`).slice(0, 40), messages: m, model: sel[0], updated: Date.now() }); setActiveSession(id); }
    syncSessions(ss);
  }, [activeSession, sel, syncSessions]);

  // load models
  useEffect(() => {
    api("/playground/models").then((m) => {
      const all = m.data || [];
      setModels(all); setLoadError(null);
      const want = sp.get("model") || sp.get("endpoint");
      const readyModels = all.filter((x) => x.live !== false && x.corex?.readiness?.ready !== false && x.corex?.routable);
      const pre = all.find((x) => x.id === want || x.corex?.endpoint_id === want);
      if (pre) setSel([pre.id]);
      else if (readyModels[0]) setSel([readyModels[0].id]);
      else if (all[0]) setSel([all[0].id]);
    }).catch((e) => { toast.error(e.message); setModels([]); setLoadError(e.message); });
  }, []);

  const pick = (id) => setSel((s) => (tab === "compare" ? (s.includes(id) ? s.filter((x) => x !== id) : [...s.slice(-1), id]) : [id]));

  // auto-scroll
  const scrollToBottom = useCallback(() => { chatRef.current?.scrollTo({ top: chatRef.current.scrollHeight, behavior: "smooth" }); }, []);
  useEffect(() => {
    const el = chatRef.current; if (!el) return;
    const check = () => setShowJump(el.scrollHeight - el.scrollTop - el.clientHeight > 100);
    el.addEventListener("scroll", check); return () => el.removeEventListener("scroll", check);
  }, []);
  useEffect(() => { if (busy) scrollToBottom(); }, [msgs, busy, scrollToBottom]);

  // stop generation
  const stop = () => { abortRef.current?.abort(); };

  // send message
  const send = async (overrideInput) => {
    const text = typeof overrideInput === "string" ? overrideInput : input;
    if (!text.trim() || busy || !sel[0]) return;
    const history = [...msgs, { role: "user", content: text }];
    setMsgs(history); setInput(""); setBusy(true); setEditIdx(null);
    const base = { messages: [{ role: "system", content: sys }, ...history], ...params };
    const controller = new AbortController(); abortRef.current = controller;
    try {
      if (tab === "chat") {
        setMsgs([...history, { role: "assistant", content: "" }]);
        const r = await streamChat({ ...base, model: sel[0] }, (t) => setMsgs([...history, { role: "assistant", content: t }]), controller.signal, runAs);
        const finalMsgs = [...history, { role: "assistant", content: r.text }];
        setMsgs(finalMsgs);
        setMetrics((prev) => ({ ...prev, [finalMsgs.length - 1]: r }));
        saveCurrentSession(finalMsgs);
      } else {
        if (sel.length < 2) { toast.error("Pick two models"); setBusy(false); return; }
        setCmp([{ text: "", model: sel[0] }, { text: "", model: sel[1] }]);
        const rs = await Promise.all(sel.map((m, i) =>
          streamChat({ ...base, model: m }, (t) => setCmp((c) => c.map((x, j) => (j === i ? { ...x, text: t } : x))), controller.signal, runAs)
            .catch((err) => ({ error: err.message, request: err.request, response: err.response }))
        ));
        setCmp(rs.map((r, i) => ({ model: sel[i], text: r.text ?? "", error: r.error, r })));
      }
    } catch (err) {
      if (err.name === "AbortError") { setMsgs(history); }
      else {
                        const why = err.status === 503 ? `Model not ready: ${err.message}` : err.status >= 500 ? `The gateway could not answer (${err.message}). Try again or pick another model.` : err.message;
        setMsgs([...history, { role: "assistant", content: "", error: why }]);
        if (err.request) setMetrics((prev) => ({ ...prev, [history.length]: { request: err.request, response: err.response, total_ms: 0 } }));
      }
    } finally { setBusy(false); abortRef.current = null; }
  };

  // regenerate
  const regenerate = () => {
    if (msgs.length < 2) return;
    const trimmed = msgs.slice(0, -1);
    setMsgs(trimmed);
    const lastUser = trimmed[trimmed.length - 1];
    if (lastUser?.role === "user") {
      setInput(""); setBusy(true);
      const base = { messages: [{ role: "system", content: sys }, ...trimmed], ...params };
      const controller = new AbortController(); abortRef.current = controller;
      setMsgs([...trimmed, { role: "assistant", content: "" }]);
      streamChat({ ...base, model: sel[0] }, (t) => setMsgs([...trimmed, { role: "assistant", content: t }]), controller.signal, runAs)
        .then((r) => {
          const final = [...trimmed, { role: "assistant", content: r.text }];
          setMsgs(final); setMetrics((prev) => ({ ...prev, [final.length - 1]: r }));
          saveCurrentSession(final);
        }).catch((err) => { toast.error(err.message); setMsgs(trimmed); })
        .finally(() => { setBusy(false); abortRef.current = null; });
    }
  };

  // edit and resend
  const editAndResend = (idx) => {
    if (msgs[idx]?.role !== "user") return;
    setEditIdx(idx);
    setInput(msgs[idx].content);
    inputRef.current?.focus();
  };
  const submitEdit = () => {
    if (editIdx == null || !input.trim()) return;
    const trimmed = msgs.slice(0, editIdx);
    setMsgs(trimmed); setEditIdx(null);
    send(input);
  };

  // keyboard
  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); editIdx != null ? submitEdit() : send(); }
  };

  // new chat
  const newChat = () => { setMsgs([]); setMetrics({}); setActiveSession(null); setInput(""); };
  const loadSession = (id) => {
    const s = sessions.find((s) => s.id === id);
    if (s) { setMsgs(s.messages || []); setActiveSession(id); if (s.model && models?.find((m) => m.id === s.model)) setSel([s.model]); }
  };
  const renameSession = (id, name) => {
    const ss = [...sessionsRef.current]; const s = ss.find((x) => x.id === id);
    if (s && name.trim()) { s.name = name.trim(); syncSessions(ss); }
  };
  const deleteSession = (id) => {
    const ss = sessionsRef.current.filter((x) => x.id !== id);
    sessionsRef.current = ss; setSessions(ss);
    api(`/playground/sessions/${id}`, { method: "DELETE" }).catch((e) => toast.error(e.message));
    if (activeSession === id) { setActiveSession(null); setMsgs([]); }
  };

  // retry on load error
  const retryLoad = () => {
    setModels(null); setLoadError(null);
    api("/playground/models").then((m) => {
      const all = m.data || [];
      setModels(all);
      const readyModels = all.filter((x) => x.live !== false && x.corex?.readiness?.ready !== false && x.corex?.routable);
      if (readyModels[0]) setSel([readyModels[0].id]); else if (all[0]) setSel([all[0].id]);
    }).catch((e) => { setModels([]); setLoadError(e.message); });
  };

  // Is the currently selected model ready?
  const selectedModel = models?.find((m) => m.id === sel[0]);
  const selReady = selectedModel?.live !== false && selectedModel?.corex?.readiness?.ready !== false && selectedModel?.corex?.routable;
  const selReason = selectedModel?.corex?.paused ? "paused by its owner" : selectedModel?.corex?.readiness?.reason;
  const selectFirstReady = () => {
    const rm = models?.find((m) => m.live !== false && m.corex?.readiness?.ready !== false && m.corex?.routable);
    if (rm) setSel([rm.id]);
  };
  const canKey = user && ["admin", "operator", "developer"].includes(user.role);

  return (
    <div data-testid="playground-page">
      <PageHeader title="Playground" subtitle="Chat with any live endpoint through the gateway pipeline — routing, validation gate, metering. No quota."
        badge={<Badge color="cyan">gateway path</Badge>}
        actions={<div className="flex items-center gap-2">
          {selectedModel && <Link to={`/endpoints/${selectedModel.corex?.endpoint_id || selectedModel.id}`} className="cx-btn cx-btn-ghost cx-btn-sm" data-testid="pg-open-endpoint">Open endpoint</Link>}
          <Button variant="secondary" size="sm" onClick={() => setCode(true)} disabled={!sel[0]} data-testid="code-drawer-btn"><Code2 size={14} /> Use in code</Button>
          {canKey && <Button variant="secondary" size="sm" onClick={() => setKeyOpen(true)} disabled={!sel[0]} data-testid="pg-create-key-btn"><Plus size={14} /> Create key</Button>}
          {!settingsCollapsed && <Button variant="ghost" size="sm" onClick={() => setSettingsCollapsed(true)} data-testid="pg-settings-toggle-hide"><Settings2 size={14} /></Button>}
          {settingsCollapsed && <Button variant="ghost" size="sm" onClick={() => setSettingsCollapsed(false)} data-testid="pg-settings-toggle-show"><Settings2 size={14} /> Settings</Button>}
        </div>} />

      {loadError && (
        <div className="mb-4 p-3 rounded-lg border border-cx-danger/40 bg-cx-danger/10 flex items-center gap-3" data-testid="pg-load-error">
          <span className="text-sm text-cx-danger">{loadError}</span>
          <button onClick={retryLoad} className="cx-btn cx-btn-secondary cx-btn-sm" data-testid="pg-retry-load"><RefreshCw size={12} /> Retry</button>
        </div>
      )}

      <div className="flex gap-4" style={{ height: "calc(100vh - 180px)" }}>
        {/* Left rail */}
        <div className="w-[240px] shrink-0 flex flex-col cx-card-static rounded-xl overflow-hidden" data-testid="pg-left-rail">
          <div className="flex-1 overflow-hidden">
            <ModelRail models={models} selected={sel} onSelect={pick} multi={tab === "compare"} search={search} setSearch={setSearch} pinned={pinned} onPin={togglePin} canDeploy={canDeploy} />
          </div>
          <SessionList sessions={sessions} activeId={activeSession} onSelect={loadSession} onNew={newChat} onRename={renameSession} onDelete={deleteSession} />
        </div>

        {/* Center chat */}
        <div className="flex-1 flex flex-col min-w-0">
          {/* Tab bar */}
          <div className="flex items-center gap-1 mb-3">
            <Segmented size="sm" value={tab} onChange={(v) => { setTab(v); setSel((s) => s.slice(0, v === "chat" ? 1 : 2)); }} testid="tab" ariaLabel="Playground mode" options={[{ value: "chat", label: "Chat" }, { value: "compare", label: "Compare" }]} />
            {tab === "compare" && sel.length === 2 && (
              <button onClick={() => setSel([sel[1], sel[0]])} className="cx-btn cx-btn-ghost !p-1 ml-1" title="Swap models" data-testid="pg-swap"><ArrowLeftRight size={13} /></button>
            )}
          </div>

          {tab === "chat" ? (
            <div className="flex-1 flex flex-col min-h-0">
              <ConnectedTo models={models} sel={sel} runAs={runAs} setRunAs={setRunAs} />
              {/* Not-ready banner */}
              {sel[0] && !selReady && selectedModel && (
                <div className="mb-2 px-4 py-2.5 rounded-lg border border-cx-amber/30 bg-cx-amber/5 flex items-center gap-3 text-xs" data-testid="pg-not-ready-banner">
                  <AlertTriangle size={14} className="text-cx-amber shrink-0" />
                  <span className="flex-1"><Mono className="font-semibold">{sel[0]}</Mono> is not ready{selReason ? `: ${selReason}` : ""}</span>
                  <button type="button" onClick={selectFirstReady} className="cx-btn cx-btn-ghost cx-btn-sm text-cx-emerald whitespace-nowrap" data-testid="pg-choose-ready">Choose a ready model</button>
                </div>
              )}
              {/* Messages */}
              <div ref={chatRef} className="flex-1 overflow-y-auto space-y-3 p-4 rounded-xl bg-black/20 border border-cx-border relative" data-testid="pg-messages">
                {msgs.length === 0 ? (
                  (sel[0] && !selReady && selectedModel) ? (
                    <div className="flex-1 flex flex-col items-center justify-center p-6 text-center gap-3" data-testid="pg-not-ready-state">
                      <div className="w-12 h-12 rounded-xl bg-cx-amber/10 flex items-center justify-center"><AlertTriangle size={24} className="text-cx-amber" /></div>
                      <h3 className="text-base font-semibold"><Mono>{sel[0]}</Mono> is not ready</h3>
                      <p className="text-xs text-cx-muted max-w-xs">{selReason || "This model cannot accept requests right now."}</p>
                      <button type="button" onClick={selectFirstReady} className="cx-btn cx-btn-secondary cx-btn-sm" data-testid="pg-choose-ready-composer">Choose a ready model</button>
                    </div>
                  ) : <EmptyState model={sel[0]} onPrompt={(p) => { setInput(p); setTimeout(() => send(p), 50); }} />
                ) : (
                  msgs.map((m, i) => (
                    <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`pg-msg-${m.role}-${i}`}>
                      <div className={`max-w-[85%] rounded-xl px-4 py-3 relative group ${
                        m.role === "user" ? "bg-cx-emerald/10 border border-cx-emerald/20" : "bg-cx-bg-elevated border border-cx-border"}`}>
                        {m.error ? (
                          <div className="text-sm text-cx-danger flex items-center gap-2" data-testid="pg-msg-error">
                            <span>{m.error}</span>
                            <button onClick={regenerate} className="cx-btn cx-btn-ghost cx-btn-sm"><RefreshCw size={11} /> Retry</button>
                          </div>
                        ) : m.role === "assistant" ? (
                          <>
                            {m.content ? <MarkdownMessage content={m.content} /> : <span className="inline-block w-2 h-4 bg-cx-emerald animate-pulse rounded-sm" data-testid="pg-cursor" />}
                            <MsgFooter r={metrics[i]} />
                          </>
                        ) : (
                          <div className="text-sm whitespace-pre-wrap">{m.content}</div>
                        )}
                        {/* Action buttons */}
                        {m.role === "user" && !busy && (
                          <button onClick={() => editAndResend(i)} className="absolute -top-2 -right-2 hidden group-hover:flex w-6 h-6 rounded-full bg-cx-bg-elevated border border-cx-border items-center justify-center hover:border-cx-emerald/40" title="Edit & resend" data-testid={`pg-edit-${i}`}><Edit3 size={10} /></button>
                        )}
                        {m.role === "assistant" && m.content && !busy && (
                          <div className="absolute -top-2 right-2 hidden group-hover:flex gap-1">
                            <button onClick={() => { navigator.clipboard?.writeText(m.content); toast.success("Copied"); }}
                              className="w-6 h-6 rounded-full bg-cx-bg-elevated border border-cx-border flex items-center justify-center hover:border-cx-emerald/40" title="Copy" data-testid={`pg-copy-${i}`}><Copy size={10} /></button>
                          </div>
                        )}
                      </div>
                    </div>
                  ))
                )}
                {/* Jump to bottom */}
                {showJump && (
                  <button onClick={scrollToBottom} className="sticky bottom-2 left-1/2 -translate-x-1/2 cx-btn cx-btn-secondary !px-3 !py-1.5 !text-xs shadow-lg z-10" data-testid="pg-jump-bottom">
                    <ArrowDown size={12} /> Jump to latest
                  </button>
                )}
              </div>

              {/* Bottom bar */}
              <div className="mt-3 flex items-end gap-2" onDrop={handleDrop} onDragOver={handleDragOver}>
                <div className="relative flex-1">
                  <textarea ref={inputRef} value={input} onChange={(e) => { setInput(e.target.value); e.target.style.height = "auto"; e.target.style.height = Math.min(160, e.target.scrollHeight) + "px"; }}
                    onKeyDown={onKeyDown}
                    placeholder={editIdx != null ? "Edit message and press Enter…" : "Type a message… (drop .txt to insert)"}
                    rows={1} className="cx-input !pr-10 resize-none min-h-[40px] max-h-[160px] overflow-y-auto"
                    data-testid="pg-input" />
                  {editIdx != null && (
                    <button onClick={() => { setEditIdx(null); setInput(""); }} className="absolute right-2 top-2 text-cx-muted hover:text-cx-text" data-testid="pg-edit-cancel"><X size={14} /></button>
                  )}
                </div>
                <div className="flex gap-1.5">
                  {busy ? (
                    <Button variant="danger" size="sm" onClick={stop} data-testid="pg-stop"><Square size={12} /> Stop</Button>
                  ) : (
                    <span title={!selReady && sel[0] ? `${sel[0]} is not ready` : undefined}>
                      <Button type="button" onClick={() => editIdx != null ? submitEdit() : send()} disabled={!input.trim() || !sel[0] || !selReady} data-testid="pg-send"><Send size={14} /></Button>
                    </span>
                  )}
                  {!busy && msgs.length >= 2 && msgs[msgs.length - 1]?.role === "assistant" && (
                    <Button variant="ghost" size="sm" onClick={regenerate} data-testid="pg-regenerate" title="Regenerate"><RefreshCw size={13} /></Button>
                  )}
                  <div className="relative">
                    <Button variant="ghost" size="sm" onClick={() => setExportOpen(!exportOpen)} disabled={msgs.length === 0} data-testid="pg-export-btn" title="Export"><Download size={13} /></Button>
                    {exportOpen && <ExportMenu messages={msgs} model={sel[0]} onClose={() => setExportOpen(false)} />}
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => { setMsgs([]); setMetrics({}); }} disabled={msgs.length === 0} data-testid="pg-clear" title="Clear"><Trash2 size={12} /></Button>
                </div>
              </div>
            </div>
          ) : (
            /* Compare tab */
            <div className="flex-1 flex flex-col min-h-0">
              <div className="flex-1 grid md:grid-cols-2 gap-3 min-h-0" data-testid="pg-compare">
                {[0, 1].map((i) => (
                  <Card key={i} className="flex flex-col min-h-0 !p-3">
                    <div className="text-xs mb-2 flex items-center gap-2">
                      {sel[i] ? <Mono className="text-cx-emerald">{sel[i]}</Mono> : <select className="cx-input !py-0.5 !text-xs !w-auto" value="" onChange={(e) => e.target.value && setSel((s) => { const n = [...s]; n[i] = e.target.value; return n.filter(Boolean); })} data-testid={`pg-compare-pick-${i}`}><option value="">pick a model…</option>{models.filter((m) => !sel.includes(m.id)).map((m) => <option key={m.id} value={m.id}>{m.id}</option>)}</select>}
                      {cmp[i]?.r && <Badge color="gray">{cmp[i].r?.total_ms} ms</Badge>}
                    </div>
                    <div className="flex-1 overflow-y-auto p-3 rounded-lg bg-black/20 border border-cx-border" data-testid={`pg-compare-${i}`}>
                      {cmp[i]?.error ? <span className="text-cx-danger text-sm">{cmp[i].error}</span>
                        : cmp[i]?.text ? <MarkdownMessage content={cmp[i].text} />
                        : <span className="text-cx-muted text-sm">—</span>}
                    </div>
                    <MsgFooter r={cmp[i]?.r} />
                  </Card>
                ))}
              </div>
              <div className="mt-3 flex gap-2">
                <textarea value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={onKeyDown}
                  placeholder="Shared prompt for both models…" rows={1}
                  className="cx-input flex-1 resize-none min-h-[40px]" data-testid="pg-input" />
                {busy ? (
                  <Button variant="danger" onClick={stop} data-testid="pg-stop"><Square size={12} /> Stop</Button>
                ) : (
                  <Button onClick={() => send()} disabled={!input.trim() || sel.length < 2} data-testid="pg-send"><Send size={14} /></Button>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Right settings */}
        <SettingsPanel sys={sys} setSys={setSys} params={params} setParams={setParams}
          onReset={() => { setParams({ ...DEFAULTS }); setSys(PRESETS[0].value); }}
          collapsed={settingsCollapsed} setCollapsed={setSettingsCollapsed} />
      </div>

      {/* Code drawer + key form (shared KeyKit) */}
      <UseKey open={code} onOpenChange={setCode} model={sel[0] || "<endpoint name>"} />
      <KeyForm open={keyOpen} onOpenChange={setKeyOpen} endpointId={selectedModel?.corex?.endpoint_id} />
    </div>
  );
}
